import * as yup from "yup";

import { Button, KIND, TButtonProps } from "kanopi-components/Button";
import React, { FC, useState } from "react";
import { Button as BaseButton } from "baseui/button";

import { Block } from "kanopi-components/Block";
import { CardBody } from "kanopi-components/Card";
import { Form } from "kanopi-components/Form";
import { FormControl } from "kanopi-components/Input";
import { HeadingSmall } from "kanopi-components/typography";
import { Select } from "kanopi-components/Select";
import { Modal } from "kanopi-components/Modal";
import { useAPI } from "kw/api/useAPI";
import { useForm } from "react-weaver";
import { useGlobal } from "kanopi-components/useGlobal";
import { useNavigate } from "@reach/router";
import { Link } from "lucide-react";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { toaster } from "kanopi-components/ToastContainer";

type TAdminCreateQuoteModal = {
  customer: TCustomer;
  products: TProduct[];
  distributionPartners: TDistributionPartner[];
  closeModal: () => void;
};

const AdminCreateQuoteModal: FC<TAdminCreateQuoteModal> = ({
  customer,
  products,
  distributionPartners,
  closeModal,
}) => {
  const navigate = useNavigate();
  const [, setHijack] = useGlobal("hijack");
  const [, setQuotingData] = useGlobal("quoting");

  const { fieldProps, errors, values, notReady } = useForm({
    validator: yup.object().shape({
      product: yup.string().required().label("Product"),
      distributionPartner: yup.string().label("Distribution Partner"),
    }),
  });

  const productLink = React.useMemo(() => {
    const productKey = values["product"];
    if (productKey) {
      return productKey
        .map((key: string) => `product=${encodeURIComponent(key)}`)
        .join("&");
    }
  }, [values]);

  const quoteUrl = React.useMemo(() => {
    return `/getquote?${productLink}&distributionPartner=${values["distributionPartner"]}`;
  }, [productLink, values["distributionPartner"]]);

  const handleSubmit = () => {
    const productKey = values["product"];

    setQuotingData({
      enabledProducts: productKey,
    });

    const shouldAutoQuote =
      products.find((p: TProduct) => p.key == productKey)?.autoQuote || false;
    const nextUrl = shouldAutoQuote ? "/autoquote" : quoteUrl;
    setHijack({
      nextUrl,
      releaseToUrl: `/admin/customers/${customer?.id}`,
    });
    navigate(`/user-hijacking/hijack/${customer?.id}`);
  };

  const handleCopy = () => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}${quoteUrl}`);
      toaster({
        kind: "INFO",
        content: "Quote link copied to clipboard!",
      });
      closeModal();
    }
  };

  return (
    <Modal onClose={() => closeModal()} closeable isOpen={true} animate autoFocus>
      <Modal.Body $style={{ marginTop: "64px" }}>
        <Form>
          <CardBody
            display="grid"
            gridGap="scale900"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingBottom="scale900"
            paddingTop="scale900"
          >
            <Block>
              <HeadingSmall margin={0}>
                Select one or more products to create a quote for
              </HeadingSmall>
            </Block>

            <Block width="100%">
              <FormControl label="Product name" error={errors?.product}>
                <Select
                  id="select-product"
                  options={products}
                  labelKey="name"
                  valueKey="key"
                  placeholder="Choose a product"
                  multi={true}
                  {...fieldProps.product}
                />
              </FormControl>
            </Block>
            <FormControl label="Partner" error={errors?.distributionPartner}>
              <Select
                id="select-distribution-partner"
                options={[{ key: "", name: "None" }, ...distributionPartners]}
                labelKey="name"
                valueKey="key"
                placeholder="Choose a partner"
                {...fieldProps.distributionPartner}
              />
            </FormControl>
            <HorizontalSpacer justifyContent="center">
              <Button
                type="submit"
                kind={KIND.secondary}
                onClick={handleSubmit}
                disabled={notReady}
              >
                Create a quote
              </Button>
              <BaseButton
                kind={KIND.secondary}
                disabled={notReady}
                onClick={handleCopy}
              >
                <Link size={20} />
              </BaseButton>
            </HorizontalSpacer>
          </CardBody>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

type TAdminCreateQuoteButton = {
  customer: TCustomer;
} & TButtonProps;

export const AdminCreateQuoteButton: FC<TAdminCreateQuoteButton> = ({
  customer,
  ...props
}) => {
  const { query } = useAPI();

  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [distributionPartners, setDistributionPartners] = useState([]);
  const getProducts = () => {
    query(
      {
        admin: {
          products: {
            name: true,
            key: true,
            autoQuote: true,
          },
          distributionPartners: {
            key: true,
            name: true,
          },
        },
      },
      (response: {
        admin: { products: TProduct[]; distributionPartners: TDistributionPartner[] };
      }) => {
        setProducts(response.admin.products);
        setDistributionPartners(response.admin.distributionPartners);
        setIsOpen(true);
      }
    );
  };

  const handleModalOpen = () => {
    if (products.length === 0) {
      getProducts();
    } else {
      setIsOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    customer?.isActive && (
      <>
        <Button
          kind={KIND.secondary}
          outlined={true}
          onClick={handleModalOpen}
          {...props}
        >
          Create a quote
        </Button>
        {isOpen && (
          <AdminCreateQuoteModal
            customer={customer}
            products={products}
            distributionPartners={distributionPartners}
            closeModal={handleModalClose}
          />
        )}
      </>
    )
  );
};
